import React, { useState, useEffect } from 'react';

// Shared "at a glance" pillow ribbon used by BOTH the Executive dashboard and each
// PM at-a-glance page. Driven by { totals, lists } (same shape from /exec/summary
// and /exec/pm/:id/summary). Click a pillow to expand its list in place; two-metric
// pillows (bids, material) cross-fade between their faces.
const card = { background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12 };
const fmtDate = d => { if (!d) return ''; const dt = new Date(String(d).length <= 10 ? d + 'T12:00:00' : d); return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
const dueLabel = d => d == null ? '' : d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? 'Due today' : `Due in ${d}d`;
const jobName = n => (n || '').replace(/^\s*\S+\s*-\s*/, '');
const timeOfDay = iso => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); };

export default function GlancePillows({ totals, lists, navigate }) {
  const [openCard, setOpenCard] = useState(null);
  const [face, setFace] = useState(0);
  // Hold the rotation while a pillow is expanded — the crew list now follows the
  // face, and swapping it out from under someone who's reading it is maddening.
  useEffect(() => {
    if (openCard) return undefined;
    const timer = setInterval(() => setFace(f => f + 1), 3500);
    return () => clearInterval(timer);
  }, [openCard]);

  const t = totals || {};
  const L = lists || {};
  // Which crew face is showing — needed outside the render loop so the expanded
  // list and the empty message can follow it.
  const crewFace = face % 2;
  // Group equipment by job so the pillow shows "job → all its equipment", most-loaded first.
  const equipGroups = Object.values((L.equipment || []).reduce((acc, e) => {
    const k = e.projectId || 'none';
    (acc[k] = acc[k] || { projectId: e.projectId, project_number: e.project_number, job_name: e.job_name, pieces: [] }).pieces.push(e);
    return acc;
  }, {})).sort((a, b) => b.pieces.length - a.pieces.length);

  const cards = [
    { key: 'jobs', icon: '🏗️', color: '#065f46', items: L.jobs || [], faces: [{ n: t.activeJobs || 0, label: 'Active jobs' }],
      emptyText: 'No active jobs.', row: it => ({ num: it.project_number, name: jobName(it.name), extra: it.days != null ? dueLabel(it.days) : '', onClick: () => navigate(`/admin/new-projects/${it.id}`) }) },
    // Two faces: who's on the clock right now, and who's SCHEDULED on our jobs. The
    // scheduled face is today's crew before noon and tomorrow's from noon on — the
    // server decides which, since it knows Reno time. Expanding follows whichever
    // face is showing, so the list always matches the number above it.
    { key: 'crew', icon: '👷', color: '#1e3a5f',
      items: crewFace === 1 ? (L.scheduledCrew || []) : (L.crew || []),
      faces: [{ n: t.crew || 0, label: 'Crew on the clock' },
              { n: t.scheduledCrew || 0, label: t.scheduledDay === 'tomorrow' ? 'Scheduled tomorrow' : 'Scheduled today' }],
      emptyText: crewFace === 1
        ? `Nobody is scheduled on your jobs ${t.scheduledDay === 'tomorrow' ? 'tomorrow' : 'today'}.`
        : 'No one is clocked in right now.',
      row: it => ({ num: it.project_number, name: jobName(it.job_name),
        extra: crewFace === 1 ? it.person : `${it.person}${it.since ? ' · ' + timeOfDay(it.since) : ''}`,
        onClick: () => navigate(`/admin/new-projects/${it.projectId}`) }) },
    { key: 'equip', icon: '🔧', color: '#b45309', items: equipGroups, faces: [{ n: t.equipment || 0, label: 'Equipment on jobs' }],
      emptyText: 'No equipment on jobs.',
      render: () => equipGroups.map((g, i) => (
        <div key={g.projectId || i} style={{ borderTop: i ? '1px solid var(--border)' : 'none', padding: '11px 14px' }}>
          <div onClick={() => g.projectId && navigate(`/admin/new-projects/${g.projectId}`)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: g.projectId ? 'pointer' : 'default', marginBottom: g.pieces.length ? 7 : 0 }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 13.5, color: '#b45309', whiteSpace: 'nowrap' }}>{g.project_number || '—'}</span>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{jobName(g.job_name)}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 800, color: '#b45309', background: '#fef3c7', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>{g.pieces.length} pc</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {g.pieces.map((p, j) => (
              <span key={j} style={{ fontSize: 12, color: '#374151', background: '#f3f4f6', borderRadius: 6, padding: '3px 8px' }}>{p.asset_number ? p.asset_number + ' · ' : ''}{p.name}</span>
            ))}
          </div>
        </div>
      )) },
    { key: 'bids', icon: '📄', color: '#3730a3', items: L.bids || [], faces: [{ n: t.bids3 || 0, label: 'Bids Due (3 days)' }, { n: t.bids || 0, label: 'Total Bids' }],
      emptyText: 'No active bids.', row: it => ({ num: '', name: it.job_name || 'Untitled bid', extra: `${(it.status || '').replace(/_/g, ' ')}${it.days != null ? ' · ' + dueLabel(it.days) : ''}`, onClick: () => navigate(`/admin/bids/${it.id}`) }) },
    { key: 'mat', icon: '🚚', color: '#0e7490', items: L.material || [], faces: [{ n: t.material || 0, label: 'Material Inbound' }, { n: t.materialWeek || 0, label: 'Inbound This Week' }],
      emptyText: 'No material inbound.', row: it => ({ num: it.project_number, name: jobName(it.project_name), extra: `${it.vendor ? it.vendor + ' · ' : ''}PO #${it.po_number} · ${fmtDate(it.ship_date)}`, onClick: () => navigate(`/admin/purchase-orders?open=${it.po_id}`) }) },
    // Overdue jobs and finished work orders nobody has billed sit together: both need
    // chasing, and unbilled work is the one that costs money quietly.
    { key: 'due', icon: '⏰', color: '#dc2626',
      items: [...(L.overdue || []).map(o => ({ ...o, kind: 'job' })), ...(L.toBill || []).map(w => ({ ...w, kind: 'bill' }))],
      faces: [{ n: t.overdue || 0, label: 'Overdue jobs' }, { n: t.toBill || 0, label: 'To bill' }],
      emptyText: 'Nothing overdue and nothing waiting to be billed — nice.',
      row: it => ({
        num: it.project_number,
        name: jobName(it.name),
        extra: it.kind === 'bill'
          ? (it.wo_status === 'ready_to_send' ? 'needs checking' : 'to bill')
          : (it.days != null ? dueLabel(it.days) : ''),
        onClick: () => navigate(it.kind === 'bill' ? '/admin/wo-billing' : `/admin/new-projects/${it.id}`),
      }) },
  ];

  return (
    <div style={{ marginBottom: 30 }}>
      <style>{'@keyframes execPillFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }'}</style>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        {cards.map(c => {
          const open = openCard === c.key;
          const fi = c.faces.length > 1 ? face % c.faces.length : 0;
          const f = c.faces[fi];
          const danger = c.key === 'due';
          return (
            <div key={c.key} onClick={() => setOpenCard(open ? null : c.key)}
              style={{ ...card, borderLeft: `4px solid ${c.color}`, padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, boxShadow: open ? `0 0 0 2px ${c.color}55` : 'none' }}>
              <div style={{ fontSize: 24, lineHeight: 1 }}>{c.icon}</div>
              <div key={fi} style={{ flex: 1, minWidth: 0, animation: c.faces.length > 1 ? 'execPillFade 0.6s ease' : 'none' }}>
                <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1, color: danger && f.n > 0 ? '#dc2626' : c.color }}>{f.n}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, fontWeight: 600 }}>{f.label}</div>
              </div>
              <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{open ? '▲' : '▼'}</span>
            </div>
          );
        })}
      </div>
      {openCard && (() => {
        const c = cards.find(x => x.key === openCard);
        if (!c) return null;
        return (
          <div style={{ ...card, marginTop: 12, padding: 4, borderLeft: `4px solid ${c.color}` }}>
            {c.items.length === 0 ? (
              <div style={{ padding: '14px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>{c.emptyText}</div>
            ) : c.render ? c.render() : c.items.map((it, i) => {
              const r = c.row(it);
              return (
                <div key={i} onClick={r.onClick}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', cursor: 'pointer', borderTop: i ? '1px solid var(--border)' : 'none' }}>
                  {r.num !== '' && r.num != null && <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 13.5, color: c.color, whiteSpace: 'nowrap' }}>{r.num}</span>}
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
                  {r.extra && <span style={{ flex: 1, minWidth: 0, textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.extra}</span>}
                  <span style={{ color: 'var(--text-muted)' }}>›</span>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}
